/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, createContext, forwardRef } from 'react';
import { type ContextValue, DEFAULT_SLOT, Link as LinkCore, LinkContext as LinkCoreContext, type LinkProps as LinkCoreProps, Provider, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import { IconContext } from '../icon/icon.component';

interface LinkIconButtonProps extends Omit<LinkCoreProps, 'style' | 'className'> {
  /** The variant of the button. (Default: primary) */
  variant?: keyof typeof variantVariants;
  /** The size of the button. (Default: 32) */
  size?: keyof typeof sizeVariants;
  /** Whether the button is rounded. (Default: false) */
  isRounded?: boolean;
  style?: stylex.StyleXStyles;
}

const LinkIconButtonContext = createContext<ContextValue<LinkIconButtonProps, HTMLAnchorElement>>({});

const LinkIconButton = forwardRef((props: LinkIconButtonProps, ref: ForwardedRef<HTMLAnchorElement>) => {
  [props, ref] = useContextProps(props, ref, LinkIconButtonContext);

  let { style, isRounded, variant = 'primary', size = 32, children, ...coreProps } = props;

  if (!coreProps['aria-label']) {
    // Show a warning instead of making it required so that aria labels can be passed through the context without having to pass them through props
    console.warn('No aria-label provided. Please provide an aria-label for accessibility.');
  }

  return (
    <LinkCore
      ref={ref}
      {...stylex.props(
        styles.base,
        variantVariants[variant],
        sizeVariants[size],
        isRounded && styles.rounded,
        coreProps.isDisabled && styles.disabled,
        style,
      )}
      {...coreProps}>
      {(renderProps) => {
        const contentColor = (
          renderProps.isDisabled && 'buttonContentDisabled' ||
          variant === 'primary' &&
          (
            (renderProps.isHovered || renderProps.isPressed) && 'primaryHover' ||
            'white'
          ) ||
          variant === 'primaryMono' && 'inverted' ||
          variant === 'secondary' && 'black' ||
          variant === 'tertiary' && 'tertiaryHover' ||
          variant === 'quaternary' &&
          (
            (renderProps.isHovered || renderProps.isPressed) && 'primary' ||
            'secondary'
          ) ||
          variant === 'alternative' && 'black' ||
          variant === 'ghost' && (
            (renderProps.isHovered || renderProps.isPressed) && 'primary' ||
            'tertiary'
          ) ||
          variant === 'destructive' && 'destructive' ||
          'interactive'
        );

        return (
          <Provider
            values={[
              [IconContext, {
                slots: {
                  [DEFAULT_SLOT]: {
                    style: iconStyles.base,
                    size: ICON_SIZES[size],
                    color: contentColor,
                  },
                  [LINK_ICON_BUTTON_SLOTS.DROPDOWN_INDICATOR]: {
                    style: iconStyles.base,
                    size: DROPDOWN_INDICATOR_SIZES[size],
                    color: contentColor,
                  },
                }
              }],
            ]}>
            {typeof children === 'function' ? children(renderProps) : children}
          </Provider>
        );
      }}
    </LinkCore>
  );
});

const ICON_SIZES: Record<keyof typeof sizeVariants, number> = {
  16: 8,
  20: 12,
  24: 12,
  28: 12,
  32: 20, // TODO: clarify this size (all buttons have 16px size icon but ghost buttons have 20px)
  40: 20,
  48: 24,
  52: 24,
  64: 24,
};

const DROPDOWN_INDICATOR_SIZES: Record<keyof typeof sizeVariants, number> = {
  16: 6,
  20: 8,
  24: 8,
  28: 12,
  32: 12,
  40: 12,
  48: 16,
  52: 20,
  64: 24,
};

const styles = stylex.create({
  base: {
    alignItems: 'center',
    borderColor: 'transparent',
    borderStyle: 'solid',
    borderWidth: 1,
    boxSizing: 'border-box',
    cursor: {
      default: 'pointer',
      ':is([data-disabled])': 'not-allowed',
      ':is([data-pending])': 'auto',
    },
    display: 'inline-flex',
    isolation: 'isolate',
    justifyContent: 'center',
    position: 'relative',
    transitionDuration: '0.2s',
    transitionProperty: 'color, background-color, border',
    transitionTimingFunction: 'easy-in-out',
    userSelect: 'none',
  },
  rounded: { borderRadius: 999 },
  disabled: {
    backgroundColor: $.buttonSurfaceDisabled,
    borderColor: 'transparent',
  },
});

const variantVariants = stylex.create({
  primary: {
    backgroundColor: {
      default: $.buttonSurfacePrimary,
      ':is([data-hovered], [data-pressed], [data-pending])': $.buttonSurfacePrimaryHover,
    },
  },
  primaryMono: {
    backgroundColor: {
      default: $.buttonSurfacePrimaryMono,
      ':is([data-hovered], [data-pressed], [data-pending])': $.buttonSurfacePrimaryMonoHover,
    },
  },
  secondary: {
    backgroundColor: {
      default: $.buttonSurfaceSecondary,
      ':is([data-hovered], [data-pressed], [data-pending])': $.buttonSurfaceSecondaryHover,
    },
  },
  tertiary: {
    backgroundColor: {
      default: $.buttonSurfaceTertiary,
      ':is([data-hovered], [data-pressed], [data-pending])': $.buttonSurfaceTertiaryHover,
    },
  },
  quaternary: {
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered], [data-pressed], [data-pending])': $.buttonSurfaceQuaternaryHover,
    },
    borderColor: {
      default: $.border10,
      ':is([data-hovered], [data-pressed], [data-pending])': 'transparent',
    },
    borderWidth: 1,
  },
  alternative: {
    backgroundColor: {
      default: $.buttonSurfaceAlternative,
      ':is([data-hovered], [data-pressed], [data-pending])': $.buttonSurfaceAlternativeHover,
    },
    boxShadow: {
      default: $.shadowBottom050Medium,
      ':is([data-pending])': $.shadowBottom050Silent,
      ':is([data-hovered], [data-pressed], [data-disabled])': 'none',
    },
  },
  ghost: {
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered], [data-pending])': $.opacity04,
      ':is([data-pressed])': $.opacity08,
    }
  },
  destructive: {
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered], [data-pressed], [data-pending])': $.buttonSurfaceGhostDestructiveHover,
    }
  },
});

const sizeVariants = stylex.create({
  16: {
    borderRadius: 4,
    gap: 4,
    height: 16,
    paddingInline: 4,
    width: 16,
  },
  20: {
    borderRadius: 5,
    gap: 4,
    height: 20,
    paddingInline: 4,
    width: 20,
  },
  24: {
    borderRadius: 6,
    gap: 4,
    height: 24,
    paddingInline: 6,
    width: 24,
  },
  28: {
    borderRadius: 6,
    gap: 4,
    height: 28,
    width: 28,
  },
  32: {
    borderRadius: 8,
    gap: 4,
    height: 32,
    paddingInline: 8,
    width: 32,
  },
  40: {
    borderRadius: 8,
    gap: 6,
    height: 40,
    paddingInline: 10,
    width: 40,
  },
  48: {
    borderRadius: 10,
    gap: 6,
    height: 48,
    width: 48,
  },
  52: {
    borderRadius: 10,
    gap: 8,
    height: 52,
    paddingInline: 12,
    width: 52,
  },
  64: {
    borderRadius: 14,
    gap: 8,
    height: 64,
    paddingInline: 20,
    width: 64,
  },
});

const iconStyles = stylex.create({
  base: {
    transitionDuration: '0.2s',
    transitionProperty: 'stroke, fill',
    transitionTimingFunction: 'easy-in-out',
  },
});

const LINK_ICON_BUTTON_SLOTS = {
  DROPDOWN_INDICATOR: 'dropdown-indicator',
};

export { LINK_ICON_BUTTON_SLOTS, LinkCore, LinkCoreContext, LinkIconButton, LinkIconButtonContext, type LinkIconButtonProps };

